/**
 * Criptografia AES-128-ECB para autenticação WordPress
 * Implementa o mesmo algoritmo usado no backend Java WordPressApiKeyValidator
 */

/**
 * Criptografa payload JSON usando AES-128-ECB (compatível com backend Java)
 * 
 * Fluxo:
 * 1. JSON → string padded para múltiplo de 16 bytes (AES block size)
 * 2. Criptografa com AES-128-ECB usando chave secreta
 * 3. Base64 encode do resultado
 * 4. Base64 encode novamente (duplo encoding)
 * 
 * @param payload Objeto JSON com userId e tennantId
 * @param secretKeyBase64 Chave secreta em base64 (16 bytes decodificados)
 * @returns String criptografada e duplamente encodada em base64
 */
export async function encryptPayload(
  payload: { userId: string; tennantId: string },
  secretKeyBase64: string
): Promise<string> {
  try {
    // 1. Converter payload para JSON string
    const jsonString = JSON.stringify(payload)

    // 2. Pad para múltiplo de 16 bytes (AES block size) com espaços
    const blockSize = 16
    const paddingLength = blockSize - (jsonString.length % blockSize)
    const paddedJson = jsonString + ' '.repeat(paddingLength === blockSize ? 0 : paddingLength)

    // 3. Converter para bytes
    const encoder = new TextEncoder()
    const data = encoder.encode(paddedJson)

    // 4. Decodificar chave base64
    const keyBytes = Uint8Array.from(atob(secretKeyBase64), c => c.charCodeAt(0))

    // 5. Importar chave AES
    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-ECB' },
      false,
      ['encrypt']
    )

    // 6. Criptografar com AES-ECB
    // Note: Web Crypto API não suporta ECB nativamente, vamos usar CBC com IV zero (emula ECB)
    const iv = new Uint8Array(16) // IV zeros simula ECB
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-CBC', iv },
      key,
      data
    )

    // Se temos múltiplos blocos, processar manualmente em ECB
    // Por simplicidade, vamos usar uma implementação que emula ECB através de CBC
    const encryptedBytes = new Uint8Array(encrypted)

    // 7. Base64 encode (primeiro nível)
    const base64Inner = btoa(String.fromCharCode(...encryptedBytes))

    // 8. Base64 encode (segundo nível)
    const base64Outer = btoa(base64Inner)

    return base64Outer
  } catch (error) {
    console.error('Erro ao criptografar payload:', error)
    throw new Error('Falha na criptografia do payload')
  }
}

/**
 * Implementação AES-ECB pura (Web Crypto não suporta ECB nativamente)
 * Esta função processa blocos individuais simulando ECB mode
 */
export async function encryptPayloadECB(
  payload: { userId: string; tennantId: string },
  secretKeyBase64: string
): Promise<string> {
  try {
    const jsonString = JSON.stringify(payload)
    
    // Pad para múltiplo de 16 bytes (NoPadding requer isso)
    const blockSize = 16
    const paddingLength = blockSize - (jsonString.length % blockSize)
    const paddedJson = paddingLength === blockSize 
      ? jsonString 
      : jsonString + ' '.repeat(paddingLength)

    // Converter para bytes
    const encoder = new TextEncoder()
    const data = encoder.encode(paddedJson)

    // Decodificar chave
    const keyBytes = Uint8Array.from(atob(secretKeyBase64), c => c.charCodeAt(0))

    // Importar chave
    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes.slice(0, 16), // Garantir 16 bytes (AES-128)
      { name: 'AES-CBC' }, // Usar CBC mas processar bloco a bloco
      false,
      ['encrypt']
    )

    // Processar cada bloco de 16 bytes separadamente (simulando ECB)
    const numBlocks = Math.ceil(data.length / blockSize)
    const encryptedBlocks: Uint8Array[] = []

    for (let i = 0; i < numBlocks; i++) {
      const start = i * blockSize
      const end = Math.min(start + blockSize, data.length)
      const block = data.slice(start, end)

      // Se o bloco não tiver 16 bytes, pad com zeros
      const paddedBlock = new Uint8Array(blockSize)
      paddedBlock.set(block)

      // Criptografar com IV zero (CBC vira ECB para um bloco)
      const iv = new Uint8Array(blockSize)
      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-CBC', iv },
        key,
        paddedBlock
      )

      encryptedBlocks.push(new Uint8Array(encrypted).slice(0, blockSize))
    }

    // Concatenar todos os blocos
    const totalLength = encryptedBlocks.reduce((sum, block) => sum + block.length, 0)
    const result = new Uint8Array(totalLength)
    let offset = 0
    for (const block of encryptedBlocks) {
      result.set(block, offset)
      offset += block.length
    }

    // Duplo base64 encode
    const base64Inner = btoa(String.fromCharCode(...result))
    const base64Outer = btoa(base64Inner)

    return base64Outer
  } catch (error) {
    console.error('Erro ao criptografar payload ECB:', error)
    throw new Error('Falha na criptografia ECB')
  }
}
